import type { EmploymentHoursRow } from '../EmploymentHours.types';

/**
 * Column groups, in display order. These are the compound header groups of the Dojo grid
 * (`EmployeeWorkAgreementsGridColumns.js`). Groups whose id ends in a "detail" concept are the ones
 * Dojo hid behind click-to-expand header links; here they are simply hidden by default and reachable
 * via the column picker or the row detail panel.
 */
export type HoursGroupId =
  | 'period'
  | 'contract'
  | 'contractDetails'
  | 'delivering'
  | 'deliveringDetails'
  | 'hoursBalance'
  | 'hoursBalanceDetails'
  | 'leave'
  | 'leaveStructure'
  | 'leaveCorrection'
  | 'leaveRecord'
  | 'leaveStatutory'
  | 'leaveCompensation'
  | 'leaveExtralegal'
  | 'plb'
  | 'plbDetails'
  | 'plbDistribution'
  | 'plbRecording'
  | 'plbSummary'
  | 'plbHistory'
  | 'llStructure'
  | 'llDeposit'
  | 'llDetails'
  | 'balanceBudget';

/**
 * The three top-level sections the groups are nested under. The Dojo grid had none — its 23 compound
 * headers sat side by side, which reads as one undifferentiated wall of ~85 columns.
 */
export type HoursSectionId = 'contractHours' | 'leave' | 'plbAndLl';

/** CLA-year flags that gate whole groups or individual columns. */
export type HoursClaFlag = 'PLB_VISIBILITY' | 'PLB_10_YEARS';

/**
 * A gate that needs every listed flag. Dojo nested the `PLB_10_YEARS` if/else inside the
 * `PLB_VISIBILITY` gate (`EmployeeWorkAgreementsGridColumns.js:579-906`), so those columns need both.
 */
export type HoursFlagGate = HoursClaFlag | HoursClaFlag[];

/**
 * How a header renders.
 * - `text` — plain translation key.
 * - `plb`  — translation containing `{{plbName}}`; replaces Dojo's `label.replace('PLB', plbName)`.
 * - `year` — a bare year number, `selectedYear - offset`. `andOlder` renders "<year> and older".
 *            `fallbackKey` is used when no year is selected yet, matching Dojo's
 *            `selectedYear ? selectedYear - n : nls.<fallback>` pattern.
 */
export type HeaderLabel =
  | { kind: 'text'; key: string }
  | { kind: 'plb'; key: string }
  | {
      kind: 'year';
      offset: number;
      fallbackKey: string;
      /** the fallback label itself needs plbName interpolation */
      fallbackIsPlb?: true;
      andOlder?: true;
    };

/** Cells that are not a plain right-aligned 2-decimal number. */
export type HoursCellRenderer =
  | 'period'
  | 'balance'
  | 'cumulativeBalance'
  /** detail window only: the balance before correction plus the correction being typed */
  | 'totalHours';

export type HoursColumnSpec = {
  /**
   * The DataGrid `field`. MUST be unique across the whole spec. It differs from `source` where the
   * Dojo grid rendered the same data key in more than one group — dgrid tolerated duplicate fields,
   * MUI silently drops them, so those columns get a `<source>:<group>` suffix and a valueGetter.
   */
  id: string;
  /** the key on the API row this column reads */
  source: keyof EmploymentHoursRow;
  group: HoursGroupId;
  label: HeaderLabel;
  /** header tooltip: `employmentHours.tooltips.<key>`; plb labels interpolate plbName too */
  tooltipKey?: string;
  render?: HoursCellRenderer;
  /** Dojo's plusMinusFormatter columns — negated when the plus/minus org setting is on */
  signFlipWhenPlusMinus?: true;
  requiresFlag?: HoursFlagGate;
  excludedByFlag?: HoursClaFlag;
  /** part of the lean default column set */
  defaultVisible?: true;
  /**
   * Shown in the month detail window only — the grid has no column for it. These are the values the
   * old correction form carried on its own: the balance before correction, the computed total, the
   * one-time allowances and the deposited balance-budget totals.
   */
  detailOnly?: true;
  /** the balance-budget rows: December only, and only with the grant */
  requiresBalanceBudget?: true;
  /** in the grid only — the detail window renders this data key somewhere else */
  gridOnly?: true;
  width?: number;
};

export type HoursGroupSpec = {
  id: HoursGroupId;
  label: HeaderLabel;
};

export type HoursSectionSpec = {
  id: HoursSectionId;
  label: HeaderLabel;
  /** the groups it spans, which must be consecutive in `HOURS_GROUPS` */
  groups: HoursGroupId[];
};

/** Terse constructors so the spec tables stay one line per column. */
export const text = (key: string): HeaderLabel => ({ kind: 'text', key });

export const plb = (key: string): HeaderLabel => ({ kind: 'plb', key });

export const year = (
  offset: number,
  fallbackKey: string,
  options?: { andOlder?: true; fallbackIsPlb?: true }
): HeaderLabel => ({
  kind: 'year',
  offset,
  fallbackKey,
  ...(options?.andOlder && { andOlder: options.andOlder }),
  ...(options?.fallbackIsPlb && { fallbackIsPlb: options.fallbackIsPlb })
});

/** Shared by the grid, the detail window and the correction form — all three had this inline. */
export const matchesClaFlags = (
  // `object &` keeps the parameter out of TS's weak-type rule, so correction rows that carry
  // neither property (memo, flag, note) can be passed straight in
  spec: object & {
    requiresFlag?: HoursFlagGate;
    excludedByFlag?: HoursClaFlag;
  },
  flags: readonly string[]
) =>
  (!spec.requiresFlag ||
    [spec.requiresFlag].flat().every((flag) => flags.includes(flag))) &&
  (!spec.excludedByFlag || !flags.includes(spec.excludedByFlag));
